import { useContext, useState } from 'react'
import * as Popover from '@radix-ui/react-popover'
import { useQuery } from '@tanstack/react-query'
import { attributesQuery } from '../../api/typeMetadata'
import { registerWidget, type CellContentProps, type CellEditorProps } from '../registry'
import { controlChrome, editorBehavior } from '../editorChrome'
import { normalizeSubStructure, type SubStructure } from '../../picker/useAttributeLevel'
import { resolveSubFieldKind, validateComposite, buildCompositeValue } from '../subFieldKinds'
import { defaultProjection, runProjection } from '../display/projections'
import type { PlaceDetails } from '../../api/client'
import { PlacePickContext, placesConfigQuery } from '../../places/places'
import { PlaceSearch } from '../../places/PlaceSearch'

/**
 * location composite (ADR-0030 / composite-pilots Spec 1).
 *
 * Wire shape: { street, number, postal_code, city, country, latitude, longitude,
 * hex_tile (derived, compute-on-read) }. Postal display is owned by the served
 * default projection. The registered READ widget is its coordinate-only
 * fallback ("lat, lon"). hex_tile is intentionally not shown in the cell — it
 * is an indexing detail, not an address.
 *
 * The WRITE editor is METADATA-DRIVEN: its fields come from the attribute's
 * declared `sub_structure` (the composite-pilots metadata), filtered to
 * read_only=false — so hex_tile never gets an input, and a future sub-field
 * appears without touching this file. Commit assembles the dict (numbers
 * parsed, empties omitted) through the normal setValue path; the API rejects
 * derived sub-field writes and recomputes them on read.
 *
 * PLACE SEARCH (2026-10-23): when the server has Google Places configured, the
 * editor opens with "Search for a place or address". Picking a suggestion
 * commits the place's `location` exactly as the server shaped it for this
 * codec (never re-assembled here), and tells a hosting form through
 * `PlacePickContext` so it can fill its other fields. Without places the
 * editor is unchanged.
 */
interface LocationValue {
  street?: string | null
  number?: string | null
  postal_code?: string | null
  city?: string | null
  country?: string | null
  latitude?: number | null
  longitude?: number | null
}

function formatCoordinates(v: LocationValue): string | null {
  if (typeof v.latitude === 'number' && typeof v.longitude === 'number') {
    return `${v.latitude.toFixed(4)}, ${v.longitude.toFixed(4)}`
  }
  return null
}

function LocationContent({ value }: CellContentProps) {
  const text =
    value && typeof value === 'object' ? formatCoordinates(value as LocationValue) : null
  if (!text) return <span className="text-muted-foreground">—</span>
  return <span className="truncate">{text}</span>
}

function LocationEditor({ value, onCommit, onCancel, context, slug, typeId, attributeId }: CellEditorProps) {
  const b = editorBehavior(context)
  const [open, setOpen] = useState(false)
  const initial = (value && typeof value === 'object' ? value : {}) as Record<string, unknown>
  const [draft, setDraft] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      Object.entries(initial)
        .filter(([, v]) => v !== null && v !== undefined)
        .map(([k, v]) => [k, String(v)]),
    ),
  )

  // Sub-fields come from the attribute's declared sub_structure (cached
  // attributes query — the same one the rest of the surface uses).
  const { data: attrs } = useQuery({
    ...attributesQuery(slug!, typeId!),
    enabled: !!slug && !!typeId,
  })
  const subFields = normalizeSubStructure(
    ((attrs ?? []) as unknown as Array<{ attribute_id: string; sub_structure?: SubStructure }>)
      .find((a) => a.attribute_id === attributeId)?.sub_structure,
  ).filter((s) => !s.read_only)
  const attribute = ((attrs ?? []) as unknown as Array<{
    attribute_id: string
    name?: string
    display_spec?: unknown
  }>).find((a) => a.attribute_id === attributeId)
  const projection = defaultProjection(attribute?.display_spec)
  const projected = projection ? runProjection(initial, projection) : null
  const triggerText = projected?.text ?? formatCoordinates(initial as LocationValue)

  // SEN-1780: never send a value the codec will refuse. The sub-field's
  // constraint KIND is served metadata, so validation and coercion both come
  // from it — no branch on a field's name, and an unregistered kind passes
  // through untouched.
  const [errors, setErrors] = useState<string[]>([])
  const { data: placesConfigured } = useQuery({ ...placesConfigQuery(slug ?? ''), enabled: !!slug })
  const onPlacePicked = useContext(PlacePickContext)
  function pickPlace(place: PlaceDetails) {
    const next = place.location
    setDraft(Object.fromEntries(
      Object.entries(next).filter(([, v]) => v !== null && v !== undefined).map(([k, v]) => [k, String(v)]),
    ))
    setErrors([])
    if (b.controlledOpen) setOpen(false)
    onCommit(next)
    onPlacePicked?.(place, attributeId)
  }
  function commit() {
    const problems = validateComposite(subFields, draft)
    if (problems.length > 0) {
      // Stay open with the reason: the wire refusal is deliberately static
      // (SEN-1071), so if this reaches the server the user is told only that
      // "the supplied value could not be encoded" — with no field named.
      setErrors(problems)
      return
    }
    setErrors([])
    if (b.controlledOpen) setOpen(false)
    onCommit(buildCompositeValue(subFields, draft))
  }

  return (
    <Popover.Root
      open={b.controlledOpen ? open : undefined}
      defaultOpen={!b.controlledOpen}
      onOpenChange={(o) => {
        if (b.controlledOpen) setOpen(o)
        if (!o && b.dismissCancels) onCancel()
      }}
    >
      <Popover.Trigger asChild>
        <button
          data-testid="cell-editor"
          className={`flex w-full items-center gap-1.5 rounded-sm px-2 py-1.5 text-left text-sm text-foreground outline-none ${controlChrome(context)}`}
          onClick={b.controlledOpen ? () => setOpen(true) : undefined}
        >
          {triggerText ?? (
            context === 'form' ? (
              <span className="text-muted-foreground">
                {(() => {
                  const name = attribute?.name
                  return name ? `Set ${name}…` : 'Set location…'
                })()}
              </span>
            ) : (
              <span className="text-muted-foreground">—</span>
            )
          )}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          align="start"
          sideOffset={4}
          collisionPadding={8}
          onOpenAutoFocus={(e) => e.preventDefault()}
          // The form this opens from can be long (a Site now carries dozens of
          // fields), leaving less room than the sub-fields need: cap the panel
          // at the room Radix measured and scroll inside it, so Save is
          // always reachable instead of falling off the viewport.
          className="z-[100] w-72 max-h-[var(--radix-popover-content-available-height)] overflow-y-auto rounded-float border border-border bg-background p-2 shadow-float"
        >
          {placesConfigured && slug && (
            <>
              <PlaceSearch slug={slug} onPick={pickPlace} />
              <div className="mb-1.5 text-[11px] text-muted-foreground">Or enter the address</div>
            </>
          )}
          {subFields.map((sf) => {
            const kind = resolveSubFieldKind(sf.kind ?? sf.type)
            return (
            <label key={sf.name} className="mb-1.5 block text-xs">
              <span className="mb-0.5 block text-muted-foreground">{sf.label ?? sf.name}</span>
              <input
                data-testid={`location-sub-${sf.name}`}
                type={kind.inputType}
                placeholder={kind.placeholder}
                value={draft[sf.name] ?? ''}
                onChange={(e) => setDraft((p) => ({ ...p, [sf.name]: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); commit() }
                  if (e.key === 'Escape') { e.preventDefault(); onCancel() }
                }}
                className={`w-full rounded-sm bg-background px-2 py-1 text-sm outline-none ${controlChrome('form')}`}
              />
              {kind.help && (
                <span data-testid={`location-hint-${sf.name}`} className="mt-0.5 block text-[11px] text-muted-foreground">
                  {kind.help}
                </span>
              )}
            </label>
            )
          })}
          {subFields.length === 0 && (
            <div className="py-1 text-xs text-muted-foreground">Loading sub-fields…</div>
          )}
          {errors.length > 0 && (
            <div data-testid="location-error" className="mt-1 space-y-0.5 text-[11px] text-destructive">
              {errors.map((e) => <div key={e}>{e}</div>)}
            </div>
          )}
          <div className="mt-1 flex justify-end">
            <button
              type="button"
              data-testid="location-save"
              onClick={commit}
              className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:bg-primary/90"
            >
              Save
            </button>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}

registerWidget('location', { read: LocationContent, write: LocationEditor, defaultWidth: 240 })
